import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CurrencySymbol from '@/components/CurrencySymbol';
import { toast } from 'sonner';
import {
  ArrowRightLeft, Plus, Clock, CheckCircle2, XCircle, Upload,
  ArrowRight, Eye, ChevronDown, ChevronUp, FileImage
} from 'lucide-react';
import { fmtNum } from '@/utils/currency';

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Pending Approval' },
  approved: { bg: 'bg-blue-100 text-blue-800', icon: CheckCircle2, label: 'Approved — Upload Slip' },
  processed: { bg: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'Processed' },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
};

export default function Transfers() {
  const { token, user } = useContext(AuthContext);
  const [transfers, setTransfers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showSlip, setShowSlip] = useState(null);
  const [showReject, setShowReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [uploadingSlip, setUploadingSlip] = useState(null);

  const [form, setForm] = useState({
    from_bank_id: '', from_account_number: '',
    to_bank_id: '', to_account_number: '',
    amount: '', reason: '', exchange_rate: ''
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tRes, bRes] = await Promise.all([
        axios.get(`${API}/accounting/transfers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounting/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTransfers(tRes.data);
      setBanks(bRes.data.banks || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Derived: selected accounts for the form
  const fromBank = banks.find(b => b.id === form.from_bank_id);
  const fromAccount = fromBank?.accounts?.find(a => a.account_number === form.from_account_number);
  const toBank = banks.find(b => b.id === form.to_bank_id);
  const toAccount = toBank?.accounts?.find(a => a.account_number === form.to_account_number);
  const needsRate = fromAccount && toAccount && fromAccount.currency !== toAccount.currency;

  const computeConverted = () => {
    if (!form.amount) return 0;
    const amt = parseFloat(form.amount);
    if (!needsRate) return amt;
    if (form.exchange_rate) return +(amt / parseFloat(form.exchange_rate));
    return 0; // will use system rate on backend
  };

  const handleCreate = async () => {
    if (!form.from_bank_id || !form.from_account_number || !form.to_bank_id || !form.to_account_number) {
      toast.error('Select both source and destination accounts'); return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    if (form.from_bank_id === form.to_bank_id && form.from_account_number === form.to_account_number) {
      toast.error('Source and destination cannot be the same'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
      };
      if (form.exchange_rate) payload.exchange_rate = parseFloat(form.exchange_rate);
      else delete payload.exchange_rate;
      await axios.post(`${API}/accounting/transfers`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transfer request created');
      setShowCreate(false);
      setForm({ from_bank_id: '', from_account_number: '', to_bank_id: '', to_account_number: '', amount: '', reason: '', exchange_rate: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSubmitting(false); }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/accounting/transfers/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transfer approved');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason is required'); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API}/accounting/transfers/${showReject}/reject`, { reason: rejectReason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transfer rejected');
      setShowReject(null);
      setRejectReason('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject'); }
    finally { setSubmitting(false); }
  };

  const handleUploadAndProcess = async (transferId, file) => {
    if (!file) return;
    setUploadingSlip(transferId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await axios.post(`${API}/accounting/transfers/upload-slip`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const slipUrl = uploadRes.data.url;
      await axios.put(`${API}/accounting/transfers/${transferId}/process`, { slip_url: slipUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transfer processed successfully');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to process'); }
    finally { setUploadingSlip(null); }
  };

  const AccountPicker = ({ label, bankId, accountNum, onSelect, testIdPrefix }) => (
    <div>
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      <div className="space-y-1.5 mt-1">
        {banks.map(bank => (
          <div key={`${testIdPrefix}-${bank.id}`}>
            <p className="text-xs font-semibold text-slate-600">{bank.bank_name}</p>
            {bank.accounts.map((acc, idx) => (
              <button key={`${testIdPrefix}-${bank.id}-${acc.account_number}-${idx}`}
                data-testid={`${testIdPrefix}-${bank.id}-${acc.account_number}`}
                onClick={() => onSelect(bank.id, acc.account_number)}
                className={`w-full flex justify-between items-center p-2 border rounded text-sm mb-1 transition-colors ${
                  bankId === bank.id && accountNum === acc.account_number
                    ? 'border-indigo-500 bg-indigo-50 font-semibold'
                    : 'hover:border-slate-400'
                }`}>
                <span>{acc.account_number} ({acc.currency})</span>
                <span className="font-mono text-xs"><CurrencySymbol currency={acc.currency} />{fmtNum(acc.balance || 0)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6 p-4 sm:p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ArrowRightLeft className="w-6 h-6 text-indigo-600" /> Transfers
            </h1>
            <p className="text-sm text-slate-500 mt-1">Transfer funds between bank accounts with approval workflow</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="new-transfer-btn">
            <Plus className="w-4 h-4 mr-1" /> New Transfer
          </Button>
        </div>

        {loading ? (
          <p className="text-center text-slate-400 py-12">Loading...</p>
        ) : transfers.length === 0 ? (
          <Card className="p-12 text-center">
            <ArrowRightLeft className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No transfers yet. Create your first one.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {transfers.map(t => {
              const st = STATUS_STYLES[t.status] || STATUS_STYLES.pending;
              const StIcon = st.icon;
              const isExpanded = expandedId === t.id;
              const canApprove = t.status === 'pending' && t.requested_by !== user?.email;
              const canProcess = t.status === 'approved';
              const canReject = t.status === 'pending' || t.status === 'approved';
              const isCrossCurrency = t.from_currency !== t.to_currency;

              return (
                <Card key={t.id} className="overflow-hidden" data-testid={`transfer-card-${t.id}`}>
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : t.id)}>
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                          <ArrowRightLeft className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">{t.from_bank_name}</span>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="text-sm font-semibold">{t.to_bank_name}</span>
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${st.bg}`}>
                              <StIcon className="w-3 h-3" /> {st.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{t.reason}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="text-lg font-bold text-slate-800">
                          <CurrencySymbol currency={t.from_currency} />{fmtNum(t.amount)}
                        </p>
                        {isCrossCurrency && (
                          <p className="text-xs text-indigo-600 font-medium">
                            <ArrowRight className="w-3 h-3 inline" /> <CurrencySymbol currency={t.to_currency} />{fmtNum(t.converted_amount)}
                          </p>
                        )}
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto mt-1" /> : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto mt-1" />}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t bg-slate-50">
                      <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                        <div>
                          <p className="text-xs text-slate-400 font-medium">From</p>
                          <p className="font-semibold">{t.from_bank_name}</p>
                          <p className="text-xs text-slate-500">{t.from_account_number} ({t.from_currency})</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 font-medium">To</p>
                          <p className="font-semibold">{t.to_bank_name}</p>
                          <p className="text-xs text-slate-500">{t.to_account_number} ({t.to_currency})</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 font-medium">Amount</p>
                          <p className="font-bold"><CurrencySymbol currency={t.from_currency} />{fmtNum(t.amount)}</p>
                          {isCrossCurrency && (
                            <p className="text-xs text-indigo-600">
                              Converts to <CurrencySymbol currency={t.to_currency} />{fmtNum(t.converted_amount)}
                              {t.exchange_rate && <span className="text-slate-500"> (Rate: {t.exchange_rate})</span>}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 font-medium">Reason</p>
                          <p className="text-slate-700">{t.reason}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 font-medium">Requested By</p>
                          <p className="text-slate-700">{t.requested_by_name}</p>
                          <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</p>
                        </div>
                        {t.approved_by && (
                          <div>
                            <p className="text-xs text-slate-400 font-medium">Approved By</p>
                            <p className="text-slate-700">{t.approved_by_name}</p>
                            <p className="text-xs text-slate-400">{t.approved_at && new Date(t.approved_at).toLocaleString()}</p>
                          </div>
                        )}
                        {t.rejected_by && (
                          <div className="col-span-2">
                            <p className="text-xs text-slate-400 font-medium">Rejected By</p>
                            <p className="text-red-600">{t.rejected_by_name}: {t.rejection_reason}</p>
                          </div>
                        )}
                        {t.slip_url && (
                          <div>
                            <p className="text-xs text-slate-400 font-medium">Transfer Slip</p>
                            <button onClick={(e) => { e.stopPropagation(); setShowSlip(t.slip_url); }}
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-0.5"
                              data-testid={`view-slip-${t.id}`}>
                              <Eye className="w-3 h-3" /> View Slip
                            </button>
                          </div>
                        )}
                        {t.processed_at && (
                          <div>
                            <p className="text-xs text-slate-400 font-medium">Processed</p>
                            <p className="text-xs text-slate-400">{new Date(t.processed_at).toLocaleString()}</p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap pt-2 border-t">
                        {canApprove && (
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); handleApprove(t.id); }}
                            data-testid={`approve-transfer-${t.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                          </Button>
                        )}
                        {canProcess && (
                          <label className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input type="file" accept="image/*" className="hidden"
                              data-testid={`upload-slip-${t.id}`}
                              onChange={(e) => {
                                if (e.target.files[0]) handleUploadAndProcess(t.id, e.target.files[0]);
                              }} />
                            <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 pointer-events-none"
                              disabled={uploadingSlip === t.id}>
                              <Upload className="w-4 h-4 mr-1" /> {uploadingSlip === t.id ? 'Processing...' : 'Upload Slip & Process'}
                            </Button>
                          </label>
                        )}
                        {canReject && (
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); setShowReject(t.id); setRejectReason(''); }}
                            data-testid={`reject-transfer-${t.id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        )}
                        {t.status === 'pending' && t.requested_by === user?.email && (
                          <p className="text-xs text-amber-600 self-center ml-2">Waiting for another admin to approve</p>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Transfer Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-indigo-600" /> New Transfer Request
              </DialogTitle>
              <DialogDescription className="sr-only">Transfer funds between bank accounts</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <AccountPicker label="From (Source) *" bankId={form.from_bank_id} accountNum={form.from_account_number}
                onSelect={(bid, acc) => setForm(f => ({ ...f, from_bank_id: bid, from_account_number: acc, exchange_rate: '' }))}
                testIdPrefix="from" />

              <div className="flex justify-center">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-indigo-600" />
                </div>
              </div>

              <AccountPicker label="To (Destination) *" bankId={form.to_bank_id} accountNum={form.to_account_number}
                onSelect={(bid, acc) => setForm(f => ({ ...f, to_bank_id: bid, to_account_number: acc, exchange_rate: '' }))}
                testIdPrefix="to" />

              <div>
                <Label className="text-xs font-medium text-slate-500">
                  Amount ({fromAccount?.currency || '...'}) *
                </Label>
                <Input type="number" step="0.01" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00" className="mt-1" data-testid="transfer-amount" />
                {fromAccount && form.amount && parseFloat(form.amount) > (fromAccount.balance || 0) && (
                  <p className="text-xs text-red-500 mt-1">Exceeds available balance of <CurrencySymbol currency={fromAccount.currency} />{fmtNum(fromAccount.balance || 0)}</p>
                )}
              </div>

              {needsRate && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Label className="text-xs font-medium text-indigo-700">
                    Exchange Rate ({fromAccount?.currency} to {toAccount?.currency})
                  </Label>
                  <p className="text-xs text-indigo-500 mb-1">
                    How many {fromAccount?.currency} per 1 {toAccount?.currency}? Leave blank for system rate.
                  </p>
                  <Input type="number" step="0.0001" value={form.exchange_rate}
                    onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))}
                    placeholder="e.g. 15.42" className="mt-1" data-testid="transfer-exchange-rate" />
                  {form.amount && form.exchange_rate && (
                    <p className="text-xs text-indigo-700 mt-1 font-medium">
                      Destination receives: <CurrencySymbol currency={toAccount?.currency} />{fmtNum(computeConverted())}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs font-medium text-slate-500">Reason *</Label>
                <Input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. Forex purchase, payroll transfer" className="mt-1" data-testid="transfer-reason" />
              </div>

              <Button onClick={handleCreate} disabled={submitting || !form.from_bank_id || !form.to_bank_id || !form.amount || !form.reason.trim()}
                className="w-full" data-testid="submit-transfer">
                {submitting ? 'Creating...' : 'Submit Transfer Request'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={!!showReject} onOpenChange={(o) => { if (!o) { setShowReject(null); setRejectReason(''); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <XCircle className="w-5 h-5" /> Reject Transfer
              </DialogTitle>
              <DialogDescription className="sr-only">Reject transfer request</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-slate-500">Reason for rejection *</Label>
                <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g. Incorrect amount, wrong account" className="mt-1" data-testid="reject-reason-input" />
              </div>
              <Button onClick={handleReject} disabled={submitting || !rejectReason.trim()}
                className="w-full bg-red-600 hover:bg-red-700" data-testid="confirm-reject">
                {submitting ? 'Rejecting...' : 'Reject Transfer'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Slip Viewer */}
        <Dialog open={!!showSlip} onOpenChange={(o) => { if (!o) setShowSlip(null); }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileImage className="w-5 h-5 text-indigo-600" /> Transfer Slip</DialogTitle>
              <DialogDescription className="sr-only">View transfer slip</DialogDescription>
            </DialogHeader>
            {showSlip && <img src={showSlip} alt="Transfer Slip" className="max-w-full max-h-[70vh] object-contain rounded border" />}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
